import BaseComponent from '../base-component/base-component'
import { Loader } from '@googlemaps/js-api-loader'
import { mapStyles } from './../../maps/styles.js'
import type { ICoordinate, ILocation, ILocationData } from './types.js'

class Map extends BaseComponent {
  styleAttribute: string
  customStyleAttribute: string
  zoomAttribute: string
  defaultZoom: number

  api_key: string | undefined

  locationsData: ILocationData[]
  locations: ILocation[]
  center: ICoordinate | null
  markers: google.maps.Marker[]

  mapStyle: string | null
  mapCustomStyle: [] | null
  zoom: number

  map: google.maps.Map

  constructor() {
    super()

    this.styleAttribute = 'data-map-style'
    this.customStyleAttribute = 'data-map-custom-style'
    this.zoomAttribute = 'data-map-zoom'
    this.defaultZoom = 10

    this.api_key = this.dataset.key

    this.locationsData = []
    this.locations = []
    this.center = { lat: 0, lng: 0 }
    this.markers = []

    this.mapStyle = this.getAttribute(this.styleAttribute)
    this.mapCustomStyle = this.toJSON(
      this.getAttribute(this.customStyleAttribute),
    )
    this.zoom = +this.getAttribute(this.zoomAttribute) || this.defaultZoom
  }

  async connectedCallback(): Promise<void> {
    window.gm_authFailure = this.handleErrors

    if (this.locationsData.length > 0) {
      await this.initMap()
    }
  }

  disconnectedCallback(): void {
    window.gm_authFailure = null
  }

  initMap = async (): Promise<void> => {
    try {
      await new Loader({
        apiKey: this.api_key,
        version: 'weekly',
      }).load()

      this.locations = this.generateMapLocations()

      this.calcMapCenter()
      this.setMap()
      this.setMarkers()
    } catch (error) {
      this.handleErrors(error)
    }
  }

  generateMapLocations = (): ILocation[] => {
    const data = []

    for (const store of this.locationsData) {
      const { id, coordinates } = store

      const location = this.generateCoordinatesFromString(coordinates)

      if (location) {
        data.push({ id, location })
      }
    }

    return data
  }

  setMap = (): void => {
    const map = new google.maps.Map(this as HTMLElement, {
      zoom: this.locations.length > 0 ? this.zoom : 0,
      center: this.center,
      styles: this.mapCustomStyle ?? mapStyles[this.mapStyle],
    })

    this.map = map
  }

  setMarkers = (): void => {
    if (this.locations.length === 0) {
      return
    }

    this.locations.map(location => {
      const marker = new google.maps.Marker({
        map: this.map,
        position: location.location,
        id: location.id,
      })

      if (this.locations.length > 1) {
        marker.addListener('click', event => {
          this.emit('markerClick', { markerId: location.id })
        })
      }

      this.markers.push(marker)
    })
  }

  generateCoordinatesFromString = (string: string): ICoordinate | undefined => {
    const array = string.split(',')

    const lat = +array[0]
    const lng = +array[1]

    if (!lat || !lng) {
      return
    }

    return { lat, lng }
  }

  calcMapCenter(): void {
    const locationsLength = this.locations.length

    if (locationsLength > 0) {
      const center = { lat: 0, lng: 0 }

      this.locations.forEach(location => {
        center.lat = center.lat + location.location.lat
        center.lng = center.lng + location.location.lng
      })

      center.lat = center.lat / locationsLength
      center.lng = center.lng / locationsLength

      this.center = center
    }
  }

  highlightMarker(id: string): void {
    this.markers.forEach(marker => {
      if (!id) {
        marker.setOpacity(1)
        return
      }

      if (marker.id === id) {
        marker.setOpacity(1)
      } else {
        marker.setOpacity(0.5)
      }
    })
  }

  toJSON = (str: string | null): [] | null => {
    try {
      const json = JSON.parse(str)
      return json
    } catch (e) {
      return null
    }
  }

  handleErrors = (error: ErrorEvent): void => {
    console.log(error)
    this.emit('mapError', {})
  }
}

export default Map
